import type { ReactNode } from 'react';
import { DocStateChip } from './ui';
import { fileSize } from './toDocumentRow';
import type { DocumentRow } from './types';

// 18f — Documents.
//
// PAGES is given full ink rather than sitting in the metadata line with the
// file size, because it is the only column that says whether the assistant got
// anything usable out of this file. 48 pages beside 2.4 MB is a manual; 1 page
// beside 2.4 MB is a scanned cover sheet that will answer nothing. That
// distinction was already in the payload and was being rendered as a footnote.
//
// The row's actions follow its state: a file still extracting has nothing to
// preview, and a failed one has nothing to open. Rendering all three verbs on
// every row and letting two of them fail is how a table teaches people that
// its buttons are decoration.

const MONO = 'font-mono';

const W = {
  check: 38,
  category: 150,
  state: 132,
  pages: 86,
  size: 92,
  actions: 84,
} as const;

export function DocumentDropZone({
  dragging,
  uploading,
  progress,
  onBrowse,
  ...handlers
}: {
  dragging: boolean;
  uploading: boolean;
  progress: number;
  onBrowse: ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...handlers}
      className="flex items-center gap-4 rounded-lg border border-dashed px-5 py-5"
      style={{
        borderColor: dragging ? 'var(--co-brass)' : 'var(--co-line-strong)',
        background: dragging ? 'var(--co-brass-wash)' : 'var(--co-selected)',
      }}
    >
      <div
        className="flex h-[38px] w-[38px] flex-none items-center justify-center rounded-[9px] border text-[16px]"
        style={{
          borderColor: 'var(--co-brass-line)',
          background: 'var(--co-brass-wash)',
          color: 'var(--co-brass-ink)',
        }}
        aria-hidden="true"
      >
        ⇪
      </div>
      <div className="min-w-0 flex-1">
        {uploading ? (
          <>
            <div className={`${MONO} text-[12.5px] tracking-[.06em] text-[var(--co-ink)]`}>
              {`UPLOADING… ${progress}%`}
            </div>
            <div className="mt-[9px] h-[3px] w-full max-w-[320px] bg-[var(--co-line)]">
              <div
                className="h-full transition-[width]"
                style={{ width: `${progress}%`, background: 'var(--co-brass-ink)' }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="text-[13.5px] text-[var(--co-ink)]">
              Drop PDFs here, or {onBrowse}
            </div>
            {/* What upload actually does, where someone deciding whether to
                upload can read it. */}
            <div className="mt-[5px] text-[12.5px] leading-[1.5] text-[var(--co-ink-3)]">
              Text is extracted and embedded so the assistant can cite specific pages.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Head({
  children,
  width,
  align = 'left',
  className = '',
}: {
  children?: ReactNode;
  width?: number;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <div
      role="columnheader"
      className={`${MONO} ${width ? 'flex-none' : 'min-w-0 flex-1'} text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)] ${
        align === 'right' ? 'text-right' : ''
      } ${className}`}
      style={{ width }}
    >
      {children}
    </div>
  );
}

export function DocumentRegisterHead({
  allSelected,
  onSelectAll,
}: {
  allSelected: boolean;
  onSelectAll: (checked: boolean) => void;
}) {
  return (
    <div
      role="row"
      className="flex items-center border-b border-[var(--co-line)] px-5 pb-[11px] pt-[14px]"
    >
      <div className="flex flex-none items-center justify-center" style={{ width: W.check }}>
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onSelectAll(e.target.checked)}
          aria-label="Select every document"
          className="h-[13px] w-[13px] border border-[var(--co-line-strong)] accent-[var(--co-ink)]"
        />
      </div>
      <Head className="pr-4">DOCUMENT</Head>
      <Head width={W.category}>CATEGORY</Head>
      <Head width={W.state}>STATE</Head>
      <Head width={W.pages} align="right">
        PAGES
      </Head>
      <Head width={W.size} align="right" className="pr-5">
        SIZE
      </Head>
      <Head width={W.actions} />
    </div>
  );
}

export function DocumentRegisterRow({
  row,
  selected,
  onToggle,
  onOpen,
  onDownload,
  onDelete,
  categoryControl,
}: {
  row: DocumentRow;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  onOpen: () => void;
  onDownload: () => void;
  onDelete: () => void;
  /** The category picker, owned by the page because it writes. */
  categoryControl: ReactNode;
}) {
  const ready = row.state.kind === 'ready';

  return (
    <div
      role="row"
      className="flex items-center border-b border-[var(--co-line-subtle)] px-5 py-[13px] last:border-b-0 hover:bg-[var(--co-selected)]"
      style={{ background: selected ? 'var(--co-selected)' : undefined }}
    >
      <div className="flex flex-none items-center justify-center" style={{ width: W.check }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Select ${row.title}`}
          className="h-[13px] w-[13px] border border-[var(--co-line-strong)] accent-[var(--co-ink)]"
        />
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-3 pr-4">
        <span
          className={`${MONO} flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[7px] border text-[8.5px] font-bold`}
          style={{
            borderColor: 'var(--co-burgundy-line)',
            background: 'var(--co-burgundy-wash)',
            color: 'var(--co-burgundy)',
          }}
          aria-hidden="true"
        >
          PDF
        </span>
        <div className="min-w-0">
          <div className="truncate text-[13.5px] font-semibold text-[var(--co-ink)]">
            {row.title}
          </div>
          <div className={`${MONO} mt-[3px] truncate text-[10.5px] text-[var(--co-ink-4)]`}>
            {row.fileName.toUpperCase()}
          </div>
          {/* A failure states its cause on the row, not behind a tooltip — the
              reason is the only thing that tells you whether to re-upload or
              to go and unlock the file. */}
          {row.state.kind === 'failed' && (
            <div className="mt-[5px] truncate text-[11.5px] text-[var(--co-burgundy)]">
              {row.state.reason}
            </div>
          )}
        </div>
      </div>

      <div className="flex-none pr-3" style={{ width: W.category }}>
        {categoryControl}
      </div>

      <div className="flex-none" style={{ width: W.state }}>
        <DocStateChip state={row.state} />
      </div>

      <div
        className={`${MONO} flex-none text-right text-[13px] font-semibold tabular-nums`}
        style={{ width: W.pages, color: row.pages === null ? 'var(--co-ink-4)' : 'var(--co-ink)' }}
      >
        {row.pages === null ? '—' : row.pages.toLocaleString()}
      </div>

      <div
        className={`${MONO} flex-none pr-5 text-right text-[11.5px] tabular-nums text-[var(--co-ink-3)]`}
        style={{ width: W.size }}
      >
        {fileSize(row.sizeBytes)}
      </div>

      <div className="flex flex-none items-center justify-end gap-1" style={{ width: W.actions }}>
        {/* Only offered when there is something to open. A file still being
            extracted has no page to show and no text to download; rendering
            the verbs anyway and letting them fail is how a table teaches
            people its buttons are decoration. */}
        {ready && (
          <>
            <button
              type="button"
              onClick={onOpen}
              aria-label={`View ${row.title}`}
              title="Open the original file in a new tab"
              className="flex h-6 w-6 items-center justify-center text-[12px] text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
            >
              ◉
            </button>
            <button
              type="button"
              onClick={onDownload}
              aria-label={`Download ${row.title}`}
              title="Download"
              className="flex h-6 w-6 items-center justify-center text-[13px] text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
            >
              ⤓
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${row.title}`}
          className="flex h-6 w-6 items-center justify-center text-[13px] text-[var(--co-ink-4)] hover:text-[var(--co-burgundy)]"
        >
          ⌫
        </button>
      </div>
    </div>
  );
}
